import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import { API, BACKEND_URL } from '@/config';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ImagePlus, X, Loader, Send } from 'lucide-react';
import { parseFrontmatter, extractLeadingHeading } from '@/lib/frontmatter';

const BlogEditor = () => {
  const { id } = useParams(); // If present, we're editing
  const navigate = useNavigate();
  const { user, token } = useContext(AuthContext);
  const fileInputRef = useRef(null);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!!id);

  const isEditing = !!id;

  useEffect(() => {
    if (!token) {
      toast.error('Debes iniciar sesión para publicar');
      navigate('/auth/login');
      return;
    }
    if (isEditing) fetchPost();
  }, [id, token]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/blog/${id}`);
      const post = response.data;
      // Only author or admin can edit
      if (user?.role !== 'admin' && post.author_id !== user?.user_id) {
        toast.error('No tienes permiso para editar este post');
        navigate('/blog');
        return;
      }
      setTitle(post.title);
      setContent(post.content);
      setCategory(post.category || '');
      setTags((post.tags || []).join(', '));
      setMetaDescription(post.meta_description || '');
      setImages(post.images || []);
    } catch (error) {
      toast.error('Post no encontrado');
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(`${API}/blog/upload-image`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        setImages(prev => [...prev, response.data.url]);
      }
      toast.success('Imagen(es) subida(s)');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir imagen');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = (idx) => {
    setImages(prev => prev.filter((_, i) => i !== idx));
  };

  // Detects "categoria: ...\n---\n# Título\n..." style blocks pasted from
  // externally-drafted content and separates the metadata from the body,
  // instead of leaving it all as visible raw text in the post.
  const handleContentPaste = (e) => {
    const pasted = e.clipboardData.getData('text');
    const parsed = parseFrontmatter(pasted);
    if (!parsed) return; // not a frontmatter block, let the normal paste happen

    e.preventDefault();
    const { title: h1Title, rest } = extractLeadingHeading(parsed.body);

    setContent(rest);
    if (h1Title && !title.trim()) setTitle(h1Title);
    if (parsed.meta.category) setCategory(parsed.meta.category);
    if (parsed.meta.tags?.length) setTags(parsed.meta.tags.join(', '));
    if (parsed.meta.meta_description) setMetaDescription(parsed.meta.meta_description);
    toast.success('Metadata detectada (categoría, tags, resumen) y separada del contenido');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) {
      toast.error('Título y contenido son requeridos');
      return;
    }

    const payload = {
      title,
      content,
      images,
      category: category.trim() || null,
      tags: tags.split(',').map(t => t.trim()).filter(Boolean),
      meta_description: metaDescription.trim() || null,
    };

    setSaving(true);
    try {
      if (isEditing) {
        await axios.put(`${API}/blog/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Post actualizado');
        navigate(`/blog/${id}`);
      } else {
        const response = await axios.post(`${API}/blog`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Post publicado');
        navigate(`/blog/${response.data.id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const resolveImg = (src) => src?.startsWith('/api') ? `${BACKEND_URL}${src}` : src;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <Navbar />
        <div className="flex justify-center py-32">
          <div className="w-10 h-10 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50" data-testid="blog-editor">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16">
        {/* Back */}
        <button
          onClick={() => navigate('/blog')}
          className="inline-flex items-center gap-1.5 text-amber-700 hover:text-amber-900 mb-6 text-sm font-medium transition-colors"
        >
          <ArrowLeft size={16} />
          Volver al Blog
        </button>

        <div className="bg-white rounded-3xl shadow-lg border-2 border-amber-100 p-8 sm:p-10">
          <h1
            className="text-3xl sm:text-4xl font-bold text-amber-900 mb-8"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            {isEditing ? 'Editar Post' : 'Nuevo Post'}
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-semibold text-amber-900 mb-2">Título</label>
              <input
                data-testid="post-title-input"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Escribe un título para tu post..."
                className="w-full bg-amber-50/50 border-2 border-amber-200 rounded-xl px-4 py-3 text-gray-900 placeholder-amber-400 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500 transition-colors"
                maxLength={200}
                required
              />
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-semibold text-amber-900 mb-2">Contenido (Markdown)</label>
              <textarea
                data-testid="post-content-input"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                onPaste={handleContentPaste}
                placeholder="Comparte tu experiencia, conocimiento o historia sobre las Líneas de Nazca... (admite Markdown: # títulos, **negrita**, listas, etc.)"
                rows={12}
                className="w-full bg-amber-50/50 border-2 border-amber-200 rounded-xl px-4 py-3 text-gray-800 placeholder-amber-400 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500 transition-colors resize-y"
                required
              />
              <p className="text-xs text-amber-700/70 mt-1.5">
                Si pegás un texto con encabezado tipo "categoria: ... / fecha: ... / ---", la
                metadata se separa automáticamente del cuerpo del post.
              </p>
            </div>

            {/* Category & Tags */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-amber-900 mb-2">Categoría</label>
                <input
                  data-testid="post-category-input"
                  type="text"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="Ej: Teorías, Historia, Viajes..."
                  className="w-full bg-amber-50/50 border-2 border-amber-200 rounded-xl px-4 py-3 text-gray-900 placeholder-amber-400 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-amber-900 mb-2">Tags (separados por coma)</label>
                <input
                  data-testid="post-tags-input"
                  type="text"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="lineas-de-nazca, colibri, teorias"
                  className="w-full bg-amber-50/50 border-2 border-amber-200 rounded-xl px-4 py-3 text-gray-900 placeholder-amber-400 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500 transition-colors"
                />
              </div>
            </div>

            {/* Meta description */}
            <div>
              <label className="block text-sm font-semibold text-amber-900 mb-2">Resumen breve (opcional)</label>
              <textarea
                data-testid="post-meta-description-input"
                value={metaDescription}
                onChange={(e) => setMetaDescription(e.target.value)}
                placeholder="Una o dos frases que resuman el post..."
                rows={2}
                className="w-full bg-amber-50/50 border-2 border-amber-200 rounded-xl px-4 py-3 text-gray-800 placeholder-amber-400 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500 transition-colors resize-y"
              />
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-semibold text-amber-900 mb-2">Imágenes</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="hidden"
              />
              <button
                type="button"
                data-testid="upload-image-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border-2 border-amber-300 rounded-full text-amber-700 hover:bg-amber-50 hover:border-amber-400 font-medium transition-colors disabled:opacity-50"
              >
                {uploading ? (
                  <><Loader size={16} className="animate-spin" /> Subiendo...</>
                ) : (
                  <><ImagePlus size={16} /> Agregar imágenes</>
                )}
              </button>

              {/* Paste an already-uploaded Cloudflare Images URL */}
              <div className="mt-3 p-3 border border-blue-200 rounded-lg bg-blue-50">
                <p className="text-xs font-medium text-blue-600 mb-2">
                  O pegá la URL de una imagen ya subida directo a Cloudflare Images
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="https://imagedelivery.net/..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    id="cloudflare-blog-image-url"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const input = document.getElementById('cloudflare-blog-image-url');
                      const url = input.value.trim();
                      if (url) {
                        setImages(prev => [...prev, url]);
                        input.value = '';
                        toast.success('Imagen vinculada');
                      } else {
                        toast.error('Ingresa una URL válida');
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Vincular
                  </button>
                </div>
              </div>

              {/* Image Preview */}
              {images.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4" data-testid="image-previews">
                  {images.map((img, idx) => (
                    <div
                      key={idx}
                      className="relative group rounded-xl overflow-hidden border-2 border-amber-200 shadow-sm"
                    >
                      <img
                        src={resolveImg(img)}
                        alt={`Imagen ${idx + 1}`}
                        className="w-full h-32 object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        className="absolute top-1 right-1 p-1.5 bg-red-600 hover:bg-red-700 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                        aria-label="Eliminar imagen"
                      >
                        <X size={14} className="text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Submit */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                data-testid="submit-post-btn"
                disabled={saving || !title.trim() || !content.trim()}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white rounded-full font-semibold shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:hover:shadow-lg"
              >
                {saving ? (
                  <><Loader size={16} className="animate-spin" /> Guardando...</>
                ) : (
                  <><Send size={16} /> {isEditing ? 'Actualizar Post' : 'Publicar Post'}</>
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate('/blog')}
                className="px-6 py-3 bg-white border-2 border-amber-200 hover:bg-amber-50 text-amber-700 rounded-full font-medium transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BlogEditor;
